import React, { useState, useEffect } from 'react';
import {
  Users, UserPlus, Star, CheckCircle, Clock,
  Trash2, ToggleLeft, ToggleRight, Send, Eye,
  AlertCircle, ThumbsUp, ThumbsDown, Minus, Search, Key, FolderPlus, X
} from 'lucide-react';
import API from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import { useAuth } from '../context/AuthContext';
import TabBar from '../components/TabBar';

// Searchable dropdown: types to filter a (potentially large, e.g. 500+ item)
// options list instead of scrolling a native <select>. `options` is an array
// of { value, label, sublabel? }.
function SearchableSelect({ options, value, onChange, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()) || o.sublabel?.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search size={13} color="var(--text-muted)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
        <input
          value={open ? query : (selected?.label || '')}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setQuery(''); setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          style={{ fontSize: 12, paddingLeft: 30, paddingRight: selected ? 26 : 10, width: '100%', boxSizing: 'border-box' }}
        />
        {selected && !open && (
          <button onClick={() => onChange('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}>
            <X size={13} color="var(--text-muted)" />
          </button>
        )}
      </div>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4, maxHeight: 240, overflowY: 'auto', background: 'var(--bg-card)', border: '1.5px solid var(--border)', borderRadius: 'var(--r-md)', boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>
          {filtered.length === 0 && <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)' }}>No matches</div>}
          {filtered.slice(0, 100).map(o => (
            <div key={o.value} onMouseDown={() => { onChange(o.value); setOpen(false); setQuery(''); }}
              style={{ padding: '9px 12px', fontSize: 12, color: 'var(--text)', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ fontWeight: 600 }}>{o.label}</div>
              {o.sublabel && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>{o.sublabel}</div>}
            </div>
          ))}
          {filtered.length > 100 && <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center' }}>Showing first 100 — keep typing to narrow it down</div>}
        </div>
      )}
    </div>
  );
}

// ── CONSENSUS PANEL ──────────────────────────────────────────────────────────
function ConsensusPanel({ submissionId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get(`/api/admin/reviewers/consensus/${submissionId}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [submissionId]);

  if (loading) return (
    <div style={cp.overlay}>
      <div style={cp.panel}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <div style={{ width: 28, height: 28, border: '3px solid var(--border)', borderTopColor: 'var(--pink)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      </div>
    </div>
  );

  const SCORE_LABELS = ['Cinematography','Screenplay/Engaging','Acting','Direction','Dialogue','Music','Editing'];
  const SCORE_KEYS = ['avg_cinematography','avg_screenplay','avg_acting','avg_direction','avg_dialogue','avg_music','avg_editing'];

  return (
    <div style={cp.overlay}>
      <div style={cp.panel} className="fade-in">
        <div style={cp.header}>
          <h3 style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)' }}>Reviewer Consensus</h3>
          <button onClick={onClose} style={{ background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', color: 'var(--text-secondary)', borderRadius: 'var(--r-sm)', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 14 }}>✕</button>
        </div>

        {!data?.consensus && !data?.scores?.length ? (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <Eye size={32} color="var(--text-muted)" style={{ margin: '0 auto 12px' }} />
            <p style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 500 }}>No reviews submitted yet for this film.</p>
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
            {/* Consensus scores */}
            {data?.consensus && (
              <div style={{ marginBottom: 24 }}>
                <div className="section-label" style={{ marginBottom: 14 }}>
                  Average Scores ({data.consensus.reviewer_count} reviewer{data.consensus.reviewer_count !== 1 ? 's' : ''})
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                  <div style={{ fontWeight: 900, fontSize: 48, color: 'var(--pink)', lineHeight: 1 }}>
                    ★ {data.consensus.avg_overall || '—'}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 6 }}>Consensus Score</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {[
                        ['recommend', 'var(--green)', <ThumbsUp size={10} />, data.consensus.recommend_count],
                        ['consider', 'var(--amber)', <Minus size={10} />, data.consensus.consider_count],
                        ['pass', 'var(--red)', <ThumbsDown size={10} />, data.consensus.pass_count],
                      ].map(([label, color, icon, count]) => parseInt(count) > 0 && (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, background: `${color}15`, border: `1px solid ${color}33`, borderRadius: 20, padding: '3px 8px' }}>
                          <span style={{ color }}>{icon}</span>
                          <span style={{ fontSize: 10, fontWeight: 800, color }}>{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {SCORE_LABELS.map((label, i) => {
                  const val = parseFloat(data.consensus[SCORE_KEYS[i]]);
                  if (!val) return null;
                  return (
                    <div key={label} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</span>
                        <span style={{ fontSize: 12, fontWeight: 800, color: val >= 7 ? 'var(--green)' : val >= 4 ? 'var(--amber)' : 'var(--red)' }}>{val}/10</span>
                      </div>
                      <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(val/10)*100}%`, background: 'linear-gradient(90deg,var(--pink),var(--pink-light))', borderRadius: 2 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Individual reviewer scores */}
            {data?.scores?.length > 0 && (
              <div>
                <div className="section-label" style={{ marginBottom: 14 }}>Individual Reviews</div>
                {data.scores.map((score, i) => (
                  <div key={score.id} className="card" style={{ padding: 18, marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Reviewer {i + 1}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 400 }}>Submitted {score.submitted_at ? new Date(score.submitted_at).toLocaleDateString() : '—'}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {score.recommendation && (
                          <span className={`badge ${score.recommendation === 'recommend' ? 'badge-green' : score.recommendation === 'consider' ? 'badge-amber' : 'badge-red'}`}>
                            {score.recommendation}
                          </span>
                        )}
                        <span style={{ fontWeight: 900, fontSize: 18, color: 'var(--pink)' }}>
                          ★ {parseFloat(score.score_overall).toFixed(1)}
                        </span>
                      </div>
                    </div>
                    {score.feedback_summary && (
                      <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)', padding: '10px 14px', marginBottom: 8 }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>Summary</div>
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, fontWeight: 400 }}>{score.feedback_summary}</p>
                      </div>
                    )}
                    {score.feedback_strengths && (
                      <div style={{ fontSize: 12, color: 'var(--green)', marginBottom: 4 }}>
                        <strong>✓ Strengths:</strong> <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>{score.feedback_strengths}</span>
                      </div>
                    )}
                    {score.feedback_improvements && (
                      <div style={{ fontSize: 12, color: 'var(--amber)' }}>
                        <strong>△ Improvements:</strong> <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>{score.feedback_improvements}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const cp = {
  overlay: { position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 300, display: 'flex', justifyContent: 'flex-end' },
  panel: { width: 560, background: 'var(--bg-card)', display: 'flex', flexDirection: 'column', height: '100vh', borderLeft: '1.5px solid var(--border)', boxShadow: '-8px 0 40px rgba(0,0,0,0.3)' },
  header: { padding: '20px 24px', borderBottom: '1.5px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
};

// ── INLINE PASSWORD RESET ────────────────────────────────────────────────────
function PasswordResetInline({ reviewer, onClose, onSaved }) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (password.length < 6) { setError('At least 6 characters'); return; }
    setSaving(true); setError('');
    try {
      await API.put(`/api/admin/reviewers/${reviewer.id}/password`, { password });
      onSaved();
    } catch (err) { setError(err.response?.data?.error || 'Failed to update password'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', borderRadius: 'var(--r-md)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder={`New password for ${reviewer.name}`}
          style={{ flex: 1, fontSize: 12, padding: '7px 10px' }}
          autoFocus
        />
        <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ padding: '6px 12px', fontSize: 11 }}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}>
          <X size={14} />
        </button>
      </div>
      {error && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6 }}>{error}</div>}
    </div>
  );
}

// ── GROUP EDITOR ─────────────────────────────────────────────────────────────
function GroupEditor({ group, allReviewers, onClose, onSaved }) {
  const { showToast } = useToast();
  const [name, setName] = useState(group?.name || '');
  const [memberIds, setMemberIds] = useState((group?.members || []).map(m => m.id));
  const [saving, setSaving] = useState(false);

  const toggleMember = (id) => setMemberIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (group) {
        await API.put(`/api/admin/reviewers/groups/${group.id}`, { name, reviewer_ids: memberIds });
      } else {
        await API.post('/api/admin/reviewers/groups', { name, reviewer_ids: memberIds });
      }
      onSaved();
    } catch (err) { showToast(err.response?.data?.error || 'Failed to save group', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="card" style={{ width: 420, padding: 24, maxHeight: '80vh', overflowY: 'auto' }}>
        <h3 style={{ fontWeight: 800, fontSize: 16, color: 'var(--text)', marginBottom: 16 }}>{group ? 'Edit Group' : 'Create Reviewer Group'}</h3>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 10, fontWeight: 800, color: 'var(--text-secondary)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Group Name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Drama Team, Senior Reviewers" style={{ fontSize: 13 }} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 10, fontWeight: 800, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Members</label>
          {allReviewers.map(r => (
            <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}>
              <input type="checkbox" checked={memberIds.includes(r.id)} onChange={() => toggleMember(r.id)} style={{ accentColor: 'var(--pink)' }} />
              {r.name} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({r.email})</span>
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary">{saving ? 'Saving...' : 'Save Group'}</button>
        </div>
      </div>
    </div>
  );
}

// ── MAIN REVIEWER MANAGEMENT ─────────────────────────────────────────────────
// mode="management" (default) — Assign Films/Bulk Assign/Groups/All
// Assignments/Performance/Access Logs, grouped into two labeled sections.
// mode="roster" — just the reviewer roster + Add Reviewer form, used inside
// the Teams > Reviewer Team tab. Everything below (state, data fetching,
// handlers) is shared regardless of mode; only the header/tab-switcher
// chrome differs, since which activeTab is even reachable is entirely
// determined by which TabBar buttons get rendered.
export default function ReviewerManagementFull({ mode = 'management' }) {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { user } = useAuth();
  // Only super admins can create reviewers or see/reset their login
  // credentials (email, password) — normal admins can view and assign
  // reviewers, but not act as a reviewer or see how to log in as one.
  const isSuperAdmin = user?.role === 'super_admin';
  const [activeTab, setActiveTab] = useState(mode === 'roster' ? 'reviewers' : 'assign');
  const [reviewers, setReviewers] = useState([]);
  const [reviewersPagination, setReviewersPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 1 });
  const [reviewersPage, setReviewersPage] = useState(1);
  const [groups, setGroups] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignmentsPagination, setAssignmentsPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 1 });
  const [assignmentsPage, setAssignmentsPage] = useState(1);
  const [assignmentsSearch, setAssignmentsSearch] = useState('');
  // Two separate views of pending films (Request 17): `pendingFilms` is the
  // real server-paginated + searched browsing list for "Films Needing
  // Review". `allPendingForSelect` is a separate, larger (up to 200) flat
  // fetch used only to populate the Manual Assignment dropdown and Bulk
  // Assign's checklist, which both need to search/select across more than
  // one page at once — SearchableSelect does its own client-side type-ahead
  // over whatever's loaded, so it needs a real list, not one page of 50.
  // 200 is a deliberate, documented ceiling here, not the old accidental
  // 50-item default-page bug this replaces.
  const [pendingFilms, setPendingFilms] = useState([]);
  const [pendingFilmsPagination, setPendingFilmsPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 1 });
  const [pendingFilmsPage, setPendingFilmsPage] = useState(1);
  const [pendingFilmsSearch, setPendingFilmsSearch] = useState('');
  const [allPendingForSelect, setAllPendingForSelect] = useState([]);
  const [stats, setStats] = useState([]);
  const [summary, setSummary] = useState({ totalReviewers: 0, activeReviewers: 0, totalAssignments: 0, completedAssignments: 0 });
  const [newReviewer, setNewReviewer] = useState({ name: '', email: '', specialty: '', password: '' });
  const [assignForm, setAssignForm] = useState({ submission_id: '', reviewer_id: '', deadline: '' });
  const [consensusFor, setConsensusFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [reviewerSearch, setReviewerSearch] = useState('');
  const [resettingPasswordFor, setResettingPasswordFor] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showGroupEditor, setShowGroupEditor] = useState(false);
  const [bulkFilmIds, setBulkFilmIds] = useState([]);
  const [bulkFilmSearch, setBulkFilmSearch] = useState('');
  const [bulkReviewerIds, setBulkReviewerIds] = useState([]);
  const [bulkGroupId, setBulkGroupId] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [viewLogs, setViewLogs] = useState([]);
  const [viewLogsTotal, setViewLogsTotal] = useState(0);
  const [viewLogsPage, setViewLogsPage] = useState(1);
  const [viewLogsLoading, setViewLogsLoading] = useState(false);
  const [viewLogsSearch, setViewLogsSearch] = useState('');
  const [viewLogsAction, setViewLogsAction] = useState('');
  const [viewLogsActionOptions, setViewLogsActionOptions] = useState([]);

  // Each list is independently paginated + searched server-side now
  // (Request 17) — these are the per-list fetchers, wired to their own
  // page/search state below. `load()` is the composite "refresh
  // everything" used after a mutation (invite, toggle, reopen, bulk
  // assign, etc.) — existing call sites throughout this file expect that.
  const loadReviewers = async () => {
    const p = new URLSearchParams({ page: reviewersPage, limit: 50 });
    if (reviewerSearch.trim()) p.append('search', reviewerSearch.trim());
    const r = await API.get(`/api/admin/reviewers?${p}`);
    setReviewers(r.data.reviewers || []);
    if (r.data.pagination) setReviewersPagination(r.data.pagination);
  };
  const loadPendingFilms = async () => {
    const p = new URLSearchParams({ page: pendingFilmsPage, limit: 50 });
    if (pendingFilmsSearch.trim()) p.append('search', pendingFilmsSearch.trim());
    const r = await API.get(`/api/admin/reviewers/pending-films?${p}`);
    setPendingFilms(r.data.films || []);
    if (r.data.pagination) setPendingFilmsPagination(r.data.pagination);
  };
  const loadAllPendingForSelect = async () => {
    const r = await API.get('/api/admin/reviewers/pending-films?limit=200');
    setAllPendingForSelect(r.data.films || []);
  };
  const loadAssignments = async () => {
    const p = new URLSearchParams({ page: assignmentsPage, limit: 50 });
    if (assignmentsSearch.trim()) p.append('search', assignmentsSearch.trim());
    const r = await API.get(`/api/admin/reviewers/assignments?${p}`);
    setAssignments(r.data.assignments || []);
    if (r.data.pagination) setAssignmentsPagination(r.data.pagination);
  };
  const loadRest = async () => {
    const [stRes, gRes, sumRes] = await Promise.all([
      API.get('/api/admin/reviewers/stats').catch(() => ({ data: { stats: [] } })),
      API.get('/api/admin/reviewers/groups').catch(() => ({ data: { groups: [] } })),
      API.get('/api/admin/reviewers/summary').catch(() => ({ data: { totalReviewers: 0, activeReviewers: 0, totalAssignments: 0, completedAssignments: 0 } })),
    ]);
    setStats(stRes.data.stats || []);
    setGroups(gRes.data.groups || []);
    setSummary(sumRes.data);
  };
  const load = () => Promise.all([loadReviewers(), loadPendingFilms(), loadAllPendingForSelect(), loadAssignments(), loadRest()]);

  useEffect(() => { load(); }, []);
  useEffect(() => { loadReviewers(); }, [reviewersPage, reviewerSearch]);
  useEffect(() => { setReviewersPage(1); }, [reviewerSearch]);
  useEffect(() => { loadPendingFilms(); }, [pendingFilmsPage, pendingFilmsSearch]);
  useEffect(() => { setPendingFilmsPage(1); }, [pendingFilmsSearch]);
  useEffect(() => { loadAssignments(); }, [assignmentsPage, assignmentsSearch]);
  useEffect(() => { setAssignmentsPage(1); }, [assignmentsSearch]);

  const handleReopenAssignment = async (a) => {
    const ok = await confirm('Reopen this review for editing? The reviewer will be able to change their scores and feedback again.', { title: 'Reopen review?', confirmText: 'Reopen' });
    if (!ok) return;
    try {
      await API.post(`/api/admin/reviewers/assignments/${a.id}/reopen`);
      showToast('Review reopened');
      load();
    } catch (err) { showToast(err.response?.data?.error || 'Failed to reopen', 'error'); }
  };

  useEffect(() => {
    if (activeTab !== 'logs') return;
    setViewLogsLoading(true);
    const p = new URLSearchParams({ page: viewLogsPage });
    if (viewLogsSearch.trim()) p.append('search', viewLogsSearch.trim());
    if (viewLogsAction) p.append('action', viewLogsAction);
    API.get(`/api/admin/reviewers/view-logs?${p}`)
      .then(r => { setViewLogs(r.data.logs); setViewLogsTotal(r.data.total); })
      .catch(() => showToast('Could not load access logs', 'error'))
      .finally(() => setViewLogsLoading(false));
  }, [activeTab, viewLogsPage, viewLogsSearch, viewLogsAction]);

  // Reset to page 1 whenever the search or category filter changes, so a
  // narrower result set doesn't leave the page stuck past its own last page.
  useEffect(() => { setViewLogsPage(1); }, [viewLogsSearch, viewLogsAction]);

  useEffect(() => {
    if (activeTab !== 'logs') return;
    API.get('/api/admin/reviewers/view-logs/actions').then(r => setViewLogsActionOptions(r.data.actions)).catch(() => {});
  }, [activeTab]);

  const handleInvite = async () => {
    if (!newReviewer.name || !newReviewer.email) return showToast('Name and email required', 'error');
    setSaving(true);
    try {
      await API.post('/api/admin/reviewers', newReviewer);
      setNewReviewer({ name: '', email: '', specialty: '', password: '' });
      setSaved('invite');
      setTimeout(() => setSaved(''), 2000);
      await load();
    } catch (err) { showToast(err.response?.data?.error || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleAssign = async () => {
    if (!assignForm.submission_id || !assignForm.reviewer_id) return showToast('Select a film and reviewer', 'error');
    setSaving(true);
    try {
      await API.post('/api/admin/reviewers/assignments', assignForm);
      setAssignForm({ submission_id: '', reviewer_id: '', deadline: '' });
      setSaved('assign');
      setTimeout(() => setSaved(''), 2000);
      await load();
    } catch (err) { showToast(err.response?.data?.error || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleAutoAssign = async (submissionId) => {
    try {
      const res = await API.post('/api/admin/reviewers/assignments/auto', { submission_id: submissionId });
      showToast(`Auto-assigned to ${res.data.assigned} reviewer(s)`);
      await load();
    } catch (err) { showToast(err.response?.data?.error || 'Auto-assign failed', 'error'); }
  };

  const handleToggle = async (id) => {
    await API.put(`/api/admin/reviewers/${id}/toggle`);
    await load();
  };

  const [notifyingNda, setNotifyingNda] = useState(false);
  const handleNotifyNdaUpdate = async () => {
    const ok = await confirm(
      'This emails every active reviewer that the confidentiality agreement has changed. They\'ll be asked to re-accept the new version before opening their next film. Only do this after you\'ve actually updated the wording.',
      { title: 'Notify reviewers of NDA update?', confirmText: 'Send Email' }
    );
    if (!ok) return;
    setNotifyingNda(true);
    try {
      const res = await API.post('/api/admin/reviewers/notify-nda-update');
      showToast(`Notified ${res.data.notified} reviewer(s)`);
    } catch (err) { showToast(err.response?.data?.error || 'Failed to send notifications', 'error'); }
    finally { setNotifyingNda(false); }
  };

  const handleDeleteGroup = async (id) => {
    const ok = await confirm('Delete this group? This does not affect existing assignments.', { title: 'Delete group?', confirmText: 'Delete' });
    if (!ok) return;
    await API.delete(`/api/admin/reviewers/groups/${id}`);
    await load();
  };

  const toggleBulkFilm = (id) => setBulkFilmIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleBulkReviewer = (id) => setBulkReviewerIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleBulkAssign = async () => {
    if (!bulkFilmIds.length) return showToast('Select at least one film', 'error');
    if (!bulkGroupId && !bulkReviewerIds.length) return showToast('Select a group or at least one reviewer', 'error');
    setBulkAssigning(true); setBulkResult(null);
    try {
      const payload = bulkGroupId
        ? { submission_ids: bulkFilmIds, group_id: bulkGroupId }
        : { submission_ids: bulkFilmIds, reviewer_ids: bulkReviewerIds };
      const res = await API.post('/api/admin/reviewers/assignments/bulk', payload);
      setBulkResult(res.data);
      setBulkFilmIds([]); setBulkReviewerIds([]); setBulkGroupId('');
      await load();
    } catch (err) { showToast(err.response?.data?.error || 'Bulk assign failed', 'error'); }
    finally { setBulkAssigning(false); }
  };

  // Reviewers list is now filtered server-side (search param on the fetch
  // above) — `reviewers` already reflects the current search + page.
  const filteredReviewers = reviewers;
  const MANAGEMENT_TABS = [
    { id: 'assign', label: 'Assign Films', icon: <Send size={13} />, badge: pendingFilmsPagination.total },
    { id: 'bulk', label: 'Bulk Assign', icon: <FolderPlus size={13} /> },
    { id: 'groups', label: 'Groups', icon: <Users size={13} />, badge: groups.length },
    { id: 'assignments', label: 'All Assignments', icon: <Clock size={13} />, badge: assignmentsPagination.total },
    { id: 'performance', label: 'Performance', icon: <Star size={13} /> },
    { id: 'logs', label: 'Access Logs', icon: <Eye size={13} /> },
  ];

  return (
    <div>
      {mode === 'management' ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <h2 style={{ fontWeight: 900, fontSize: 24, color: 'var(--text)', letterSpacing: '-0.01em', marginBottom: 4 }}>Reviewer Management</h2>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 400 }}>
                Reviewers see only film details — never filmmaker names, emails, or phone numbers. The reviewer roster itself now lives under Teams → Reviewer Team.
              </p>
            </div>
          </div>

          {/* Stats row — compact left-aligned pills instead of 4 full-width
              stretched cards, which took up far more space than 4 small
              numbers need. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
            {[
              ['Total Reviewers', summary.totalReviewers, 'var(--pink)', <Users size={12} />],
              ['Active', summary.activeReviewers, 'var(--green)', <CheckCircle size={12} />],
              ['Total Assigned', summary.totalAssignments, 'var(--blue)', <Send size={12} />],
              ['Completed', summary.completedAssignments, 'var(--amber)', <Star size={12} />],
            ].map(([label, val, color, icon]) => (
              <div key={label} className="card" style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 12px' }}>
                <span style={{ color, display: 'flex' }}>{icon}</span>
                <span style={{ fontWeight: 800, fontSize: 14, color }}>{val}</span>
                <span style={{ fontSize: 10.5, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
              </div>
            ))}
          </div>

          {/* Single compact row — was two stacked, labeled groups; combined
              per feedback that splitting them onto separate lines wasted
              vertical space for no real benefit. */}
          <div style={{ marginBottom: 20, borderBottom: '1.5px solid var(--border)' }}>
            <TabBar tabs={MANAGEMENT_TABS} active={activeTab} onChange={setActiveTab} size="sm" />
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button onClick={handleNotifyNdaUpdate} disabled={notifyingNda} className="btn-ghost" style={{ fontSize: 11, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Send size={12} /> {notifyingNda ? 'Sending...' : 'Notify NDA Update'}
          </button>
        </div>
      )}

      {/* ── REVIEWERS LIST ── */}
      {activeTab === 'reviewers' && (
        <div className="admin-two-col" style={{ gridTemplateColumns: isSuperAdmin ? '1fr 340px' : '1fr', gap: 24 }}>
          <div>
            <div style={{ position: 'relative', marginBottom: 14 }}>
              <Search size={14} color="var(--text-muted)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
              <input
                value={reviewerSearch}
                onChange={e => setReviewerSearch(e.target.value)}
                placeholder="Search reviewers by name, email, or specialty..."
                style={{ fontSize: 13, padding: '9px 12px 9px 34px', width: '100%' }}
              />
            </div>
            {filteredReviewers.length === 0 ? (
              <div className="card" style={{ padding: 32, textAlign: 'center' }}>
                <UserPlus size={32} color="var(--text-muted)" style={{ margin: '0 auto 12px' }} />
                <p style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 500 }}>
                  {reviewers.length === 0 ? 'No reviewers yet. Add your first reviewer →' : 'No reviewers match your search.'}
                </p>
              </div>
            ) : filteredReviewers.map(r => (
              <div key={r.id} className="card" style={{ padding: 18, marginBottom: 10, borderColor: r.is_active ? 'var(--border)' : 'rgba(255,68,68,0.2)', opacity: r.is_active ? 1 : 0.6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: r.is_active ? 'var(--pink)' : 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: r.is_active ? '#fff' : 'var(--text-muted)', flexShrink: 0 }}>
                    {(r.name || r.email)[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{r.name}</span>
                      <span className={`badge ${r.is_active ? 'badge-green' : 'badge-red'}`}>{r.is_active ? 'Active' : 'Inactive'}</span>
                      <span className={`badge ${r.nda_current ? 'badge-green' : 'badge-amber'}`} title={r.nda_current ? `Accepted ${new Date(r.nda_accepted_at).toLocaleString('en-IN')}` : r.nda_accepted_at ? 'Accepted an older version of the agreement — needs to re-accept the current one' : 'Has not accepted the confidentiality agreement yet'}>
                        {r.nda_current ? '🔒 NDA Signed' : '⚠ NDA Pending'}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 400, overflowWrap: 'anywhere' }}>{r.email}</div>
                    {r.specialty && <div style={{ fontSize: 11, color: 'var(--blue)', fontWeight: 600, marginTop: 2 }}>{r.specialty}</div>}
                    <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, flexWrap: 'wrap' }}>
                      <span>📋 {r.total_assigned || 0} assigned</span>
                      <span>✓ {r.total_completed || 0} completed</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {isSuperAdmin && (
                      <button onClick={() => setResettingPasswordFor(resettingPasswordFor === r.id ? null : r.id)}
                        style={{ background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)', color: 'var(--blue)', display: 'flex', alignItems: 'center', gap: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        <Key size={11} /> Password
                      </button>
                    )}
                    <button onClick={() => handleToggle(r.id)}
                      style={{ background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)', color: r.is_active ? 'var(--red)' : 'var(--green)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {r.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>
                {isSuperAdmin && resettingPasswordFor === r.id && (
                  <PasswordResetInline
                    reviewer={r}
                    onClose={() => setResettingPasswordFor(null)}
                    onSaved={() => setResettingPasswordFor(null)}
                  />
                )}
              </div>
            ))}
            {reviewersPagination.totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginTop: 14 }}>
                <button onClick={() => setReviewersPage(p => Math.max(1, p - 1))} disabled={reviewersPage <= 1} className="btn-ghost" style={{ fontSize: 11, padding: '6px 14px' }}>← Prev</button>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Page {reviewersPagination.page} of {reviewersPagination.totalPages} · {reviewersPagination.total} total</span>
                <button onClick={() => setReviewersPage(p => Math.min(reviewersPagination.totalPages, p + 1))} disabled={reviewersPage >= reviewersPagination.totalPages} className="btn-ghost" style={{ fontSize: 11, padding: '6px 14px' }}>Next →</button>
              </div>
            )}
          </div>

          {/* Add reviewer form — super admin only, sets login credentials */}
          {isSuperAdmin && (
            <div className="card" style={{ padding: 22, height: 'fit-content' }}>
              <h3 style={{ fontWeight: 800, fontSize: 16, color: 'var(--text)', marginBottom: 18 }}>Add Reviewer</h3>
              {[['name','Name *','text','Full name'],['email','Email *','email','reviewer@email.com'],['specialty','Specialty','text','e.g. Drama, Cinematography'],['password','Password','password','Set login password']].map(([k,l,t,p]) => (
                <div key={k} style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 10, fontWeight: 800, color: 'var(--text-secondary)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{l}</label>
                  <input type={t} value={newReviewer[k]} onChange={e => setNewReviewer(f => ({ ...f, [k]: e.target.value }))} placeholder={p} style={{ fontSize: 13 }} />
                </div>
              ))}
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14, fontWeight: 400 }}>
                Reviewer can log in via Google (if email matches) or with the password you set.
              </p>
              <button onClick={handleInvite} disabled={saving} className="btn-primary" style={{ width: '100%' }}>
                {saved === 'invite' ? '✓ Reviewer Added!' : saving ? 'Adding...' : 'Add Reviewer'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── ASSIGN FILMS ── */}
      {activeTab === 'assign' && (
        <div className="admin-two-col" style={{ gridTemplateColumns: '1fr 340px', gap: 24 }}>
          <div>
            <h3 style={{ fontWeight: 800, fontSize: 16, color: 'var(--text)', marginBottom: 12 }}>Films Needing Review ({pendingFilmsPagination.total})</h3>
            <div style={{ position: 'relative', marginBottom: 14 }}>
              <Search size={13} color="var(--text-muted)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
              <input value={pendingFilmsSearch} onChange={e => setPendingFilmsSearch(e.target.value)} placeholder="Search by title, genre, or language…"
                style={{ fontSize: 12, paddingLeft: 30, width: '100%', boxSizing: 'border-box' }} />
            </div>
            {pendingFilms.map(sub => {
              // Each film's assignments come inline from the pending-films
              // endpoint (Request 17) — scoped per-page server-side, not
              // filtered client-side out of every assignment ever made.
              const filmAssignments = sub.assignments || [];
              const assignCount = filmAssignments.length;
              const completedCount = filmAssignments.filter(a => a.status === 'completed').length;
              // Sort completed-first so filled rings lead and empty ones
              // trail — reads left-to-right as "progress so far".
              const sortedAssignments = [...filmAssignments].sort((a, b) => (b.status === 'completed') - (a.status === 'completed'));
              return (
                <div key={sub.id} className="card" style={{ padding: 16, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>{sub.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{[sub.genre, sub.language].filter(Boolean).join(' · ')}</div>
                      <div style={{ display: 'flex', gap: 3, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        {/* Each ring is one assigned reviewer — filled once
                            that specific reviewer's review is submitted
                            (status='completed'), an empty outline while
                            still assigned/in-progress. No fixed cap at 2 —
                            2 is just the recommended minimum. */}
                        {sortedAssignments.map(a => (
                          <div key={a.id} title={`${a.reviewer_name || 'Reviewer'} — ${a.status === 'completed' ? 'submitted' : a.status === 'in_progress' ? 'in progress' : 'not started'}`}
                            style={{ width: 14, height: 14, borderRadius: '50%', background: a.status === 'completed' ? 'var(--pink)' : 'transparent', border: `2px solid ${a.status === 'completed' ? 'var(--pink)' : 'var(--amber)'}` }} />
                        ))}
                        <span style={{ fontSize: 10, color: completedCount >= 2 ? 'var(--text-muted)' : 'var(--amber)', fontWeight: 600, marginLeft: 4 }}>
                          {assignCount === 0 ? 'No reviewers assigned yet' : `${completedCount}/${assignCount} review${assignCount !== 1 ? 's' : ''} submitted`}
                          {assignCount > 0 && assignCount < 2 ? ' · needs 1 more reviewer' : ''}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => setConsensusFor(sub.id)} style={{ background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', color: 'var(--text-secondary)', borderRadius: 'var(--r-sm)', padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        View Reviews
                      </button>
                      {assignCount < 2 && (
                        <button onClick={() => handleAutoAssign(sub.id)} className="btn-primary" style={{ padding: '5px 10px', fontSize: 11 }}>
                          Auto-assign
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {pendingFilms.length === 0 && (
              <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                {pendingFilmsSearch ? `No pending films match "${pendingFilmsSearch}"` : 'No films pending review'}
              </div>
            )}
            {pendingFilmsPagination.totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginTop: 14 }}>
                <button onClick={() => setPendingFilmsPage(p => Math.max(1, p - 1))} disabled={pendingFilmsPage <= 1} className="btn-ghost" style={{ fontSize: 11, padding: '6px 14px' }}>← Prev</button>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Page {pendingFilmsPagination.page} of {pendingFilmsPagination.totalPages}</span>
                <button onClick={() => setPendingFilmsPage(p => Math.min(pendingFilmsPagination.totalPages, p + 1))} disabled={pendingFilmsPage >= pendingFilmsPagination.totalPages} className="btn-ghost" style={{ fontSize: 11, padding: '6px 14px' }}>Next →</button>
              </div>
            )}
          </div>

          {/* Manual assign form */}
          <div className="card" style={{ padding: 22, height: 'fit-content' }}>
            <h3 style={{ fontWeight: 800, fontSize: 16, color: 'var(--text)', marginBottom: 18 }}>Manual Assignment</h3>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 800, color: 'var(--text-secondary)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Select Film</label>
              <SearchableSelect
                placeholder="Type to search films…"
                value={assignForm.submission_id}
                onChange={v => setAssignForm(f => ({ ...f, submission_id: v }))}
                options={allPendingForSelect.map(s => ({ value: s.id, label: s.title, sublabel: [s.genre, s.language].filter(Boolean).join(' · ') }))}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 800, color: 'var(--text-secondary)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Select Reviewer</label>
              <SearchableSelect
                placeholder="Type to search reviewers…"
                value={assignForm.reviewer_id}
                onChange={v => setAssignForm(f => ({ ...f, reviewer_id: v }))}
                options={reviewers.filter(r => r.is_active).map(r => ({ value: r.id, label: r.name, sublabel: r.specialty || 'Reviewer' }))}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 800, color: 'var(--text-secondary)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Deadline (optional)</label>
              <input type="date" value={assignForm.deadline} onChange={e => setAssignForm(f => ({ ...f, deadline: e.target.value }))} style={{ fontSize: 12 }} />
            </div>
            <button onClick={handleAssign} disabled={saving} className="btn-primary" style={{ width: '100%' }}>
              {saved === 'assign' ? '✓ Assigned!' : 'Assign Film'}
            </button>
          </div>
        </div>
      )}

      {/* ── BULK ASSIGN ── */}
      {activeTab === 'bulk' && (
        <div className="admin-two-col" style={{ gridTemplateColumns: '1fr 340px', gap: 24 }}>
          <div>
            <h3 style={{ fontWeight: 800, fontSize: 16, color: 'var(--text)', marginBottom: 4 }}>Select Films ({bulkFilmIds.length} selected)</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14, fontWeight: 400 }}>
              Pick multiple films needing review, then assign them all to a reviewer group (or hand-picked reviewers) in one click. No cap on reviewers per film — 2 is just the recommended minimum.
            </p>
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <Search size={13} color="var(--text-muted)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
              <input value={bulkFilmSearch} onChange={e => setBulkFilmSearch(e.target.value)} placeholder="Search films by title, genre, or language…"
                style={{ fontSize: 12, paddingLeft: 30, width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div className="card" style={{ overflow: 'hidden', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1.5px solid var(--border)', background: 'var(--bg-elevated)' }}>
                <input type="checkbox"
                  checked={bulkFilmIds.length === allPendingForSelect.length && allPendingForSelect.length > 0}
                  onChange={e => setBulkFilmIds(e.target.checked ? allPendingForSelect.map(s => s.id) : [])} />
                <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Select All Pending ({allPendingForSelect.length})</span>
              </div>
              {allPendingForSelect.filter(sub => !bulkFilmSearch.trim() || [sub.title, sub.genre, sub.language].filter(Boolean).some(v => v.toLowerCase().includes(bulkFilmSearch.toLowerCase()))).map(sub => {
                const checked = bulkFilmIds.includes(sub.id);
                return (
                  <label key={sub.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                    borderBottom: '1px solid var(--border)', cursor: 'pointer',
                    background: checked ? 'var(--pink-glow)' : 'transparent',
                    transition: 'background 0.15s',
                  }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleBulkFilm(sub.id)} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{sub.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{[sub.genre, sub.language].filter(Boolean).join(' · ')}</div>
                    </div>
                    {checked && <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--pink)', textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>Selected</span>}
                  </label>
                );
              })}
              {allPendingForSelect.length === 0 && <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No films pending review</div>}
              {allPendingForSelect.length > 0 && allPendingForSelect.filter(sub => !bulkFilmSearch.trim() || [sub.title, sub.genre, sub.language].filter(Boolean).some(v => v.toLowerCase().includes(bulkFilmSearch.toLowerCase()))).length === 0 && (
                <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No films match "{bulkFilmSearch}"</div>
              )}
            </div>
            {bulkResult && (
              <div style={{ background: 'rgba(0,200,150,0.08)', border: '1.5px solid rgba(0,200,150,0.25)', borderRadius: 'var(--r-md)', padding: '12px 16px', color: 'var(--green)', fontSize: 13, fontWeight: 600 }}>
                ✓ Created {bulkResult.created} assignment{bulkResult.created !== 1 ? 's' : ''}
                {bulkResult.skipped_full > 0 && ` · ${bulkResult.skipped_full} skipped (film already had 2 reviewers)`}
                {bulkResult.skipped_existing > 0 && ` · ${bulkResult.skipped_existing} skipped (already assigned)`}
              </div>
            )}
          </div>

          <div className="card" style={{ padding: 22, height: 'fit-content' }}>
            <h3 style={{ fontWeight: 800, fontSize: 16, color: 'var(--text)', marginBottom: 16 }}>Assign To</h3>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 10, fontWeight: 800, color: 'var(--text-secondary)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Reviewer Group</label>
              <select value={bulkGroupId} onChange={e => { setBulkGroupId(e.target.value); if (e.target.value) setBulkReviewerIds([]); }} style={{ fontSize: 12 }}>
                <option value="">— No group, pick reviewers below —</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name} ({(g.members || []).length})</option>)}
              </select>
            </div>
            {!bulkGroupId && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 800, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Or Pick Reviewers</label>
                {reviewers.filter(r => r.is_active).map(r => {
                  const checked = bulkReviewerIds.includes(r.id);
                  return (
                    <label key={r.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', marginBottom: 2,
                      borderRadius: 'var(--r-sm)', cursor: 'pointer', fontSize: 12.5, color: 'var(--text)',
                      background: checked ? 'var(--pink-glow)' : 'transparent', transition: 'background 0.15s',
                    }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleBulkReviewer(r.id)} />
                      {r.name}
                    </label>
                  );
                })}
                {reviewers.filter(r => r.is_active).length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '6px 0' }}>No active reviewers available</div>
                )}
              </div>
            )}
            <button onClick={handleBulkAssign} disabled={bulkAssigning} className="btn-primary" style={{ width: '100%' }}>
              {bulkAssigning ? 'Assigning...' : `Bulk Assign ${bulkFilmIds.length} Film${bulkFilmIds.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

      {/* ── GROUPS ── */}
      {activeTab === 'groups' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 400 }}>Group reviewers together to assign whole teams to films at once.</p>
            <button onClick={() => { setEditingGroup(null); setShowGroupEditor(true); }} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px' }}>
              <FolderPlus size={14} /> New Group
            </button>
          </div>
          {groups.length === 0 ? (
            <div className="card" style={{ padding: 32, textAlign: 'center' }}>
              <Users size={32} color="var(--text-muted)" style={{ margin: '0 auto 12px' }} />
              <p style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 500 }}>No reviewer groups yet. Create one to speed up bulk assignment.</p>
            </div>
          ) : groups.map(g => (
            <div key={g.id} className="card" style={{ padding: 18, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)', marginBottom: 6 }}>{g.name}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(g.members || []).length === 0
                      ? <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No members yet</span>
                      : g.members.map(m => <span key={m.id} className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>{m.name}</span>)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => { setEditingGroup(g); setShowGroupEditor(true); }} style={{ background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', color: 'var(--text-secondary)', borderRadius: 'var(--r-sm)', padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Edit</button>
                  <button onClick={() => handleDeleteGroup(g.id)} style={{ background: 'none', color: 'var(--red)', border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)' }}>Delete</button>
                </div>
              </div>
            </div>
          ))}
          {showGroupEditor && (
            <GroupEditor
              group={editingGroup}
              allReviewers={reviewers.filter(r => r.is_active)}
              onClose={() => setShowGroupEditor(false)}
              onSaved={() => { setShowGroupEditor(false); load(); }}
            />
          )}
        </div>
      )}

      {/* ── ALL ASSIGNMENTS ── */}
      {activeTab === 'assignments' && (
        <div>
          <div style={{ position: 'relative', marginBottom: 12 }}>
            <Search size={13} color="var(--text-muted)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input value={assignmentsSearch} onChange={e => setAssignmentsSearch(e.target.value)} placeholder="Search by film title or reviewer name…"
              style={{ fontSize: 12, paddingLeft: 30, width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div className="card" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
            <div className="admin-grid-header" style={{ gridTemplateColumns: '1fr 140px 120px 100px 130px', padding: '9px 16px', borderBottom: '1.5px solid var(--border)', fontSize: 10.5, color: 'var(--text-secondary)', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              <span>Film</span><span>Reviewer</span><span>Status</span><span>Score</span><span>Actions</span>
            </div>
            {assignments.length === 0 && (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                {assignmentsSearch ? `No assignments match "${assignmentsSearch}"` : 'No assignments yet'}
              </div>
            )}
            {assignments.map((a, i) => (
              <div key={a.id} className="admin-grid-row" style={{ gridTemplateColumns: '1fr 140px 120px 100px 130px', padding: '11px 16px', borderBottom: i < assignments.length - 1 ? '1px solid var(--border)' : 'none', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{a.film_title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 400 }}>{[a.genre, a.language].filter(Boolean).join(' · ')}</div>
                </div>
                <div data-label="Reviewer" style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{a.reviewer_name}</div>
                <span data-label="Status" className={`badge ${a.status === 'completed' ? 'badge-green' : a.status === 'in_progress' ? 'badge-amber' : ''}`}
                  style={!['completed','in_progress'].includes(a.status) ? { background:'var(--bg-elevated)', color:'var(--text-muted)', border:'1px solid var(--border)' } : {}}>
                  {a.status.replace('_',' ')}
                </span>
                <span data-label="Score" style={{ fontWeight: 900, fontSize: 14, color: a.score_overall ? 'var(--pink)' : 'var(--text-muted)' }}>
                  {a.score_overall ? `★ ${parseFloat(a.score_overall).toFixed(1)}` : '—'}
                </span>
                <div data-label="Actions" style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setConsensusFor(a.submission_id)} style={{ background: 'none', color: 'var(--blue)', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Reviews
                  </button>
                  {a.status === 'completed' && (
                    <button onClick={() => handleReopenAssignment(a)} style={{ background: 'none', color: 'var(--amber)', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Reopen
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {assignmentsPagination.totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginTop: 14 }}>
              <button onClick={() => setAssignmentsPage(p => Math.max(1, p - 1))} disabled={assignmentsPage <= 1} className="btn-ghost" style={{ fontSize: 11, padding: '6px 14px' }}>← Prev</button>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Page {assignmentsPagination.page} of {assignmentsPagination.totalPages} · {assignmentsPagination.total} total</span>
              <button onClick={() => setAssignmentsPage(p => Math.min(assignmentsPagination.totalPages, p + 1))} disabled={assignmentsPage >= assignmentsPagination.totalPages} className="btn-ghost" style={{ fontSize: 11, padding: '6px 14px' }}>Next →</button>
            </div>
          )}
        </div>
      )}

      {/* ── PERFORMANCE ── */}
      {activeTab === 'performance' && (
        <div>
          <div className="card" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
            <div className="admin-grid-header" style={{ gridTemplateColumns: '1fr 100px 100px 100px 100px', padding: '9px 16px', borderBottom: '1.5px solid var(--border)', fontSize: 10.5, color: 'var(--text-secondary)', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              <span>Reviewer</span><span>Assigned</span><span>Completed</span><span>Pending</span><span>Avg Score Given</span>
            </div>
            {stats.map((r, i) => (
              <div key={r.id} className="admin-grid-row" style={{ gridTemplateColumns: '1fr 100px 100px 100px 100px', padding: '12px 16px', borderBottom: i < stats.length - 1 ? '1px solid var(--border)' : 'none', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{r.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 400 }}>{r.specialty || r.email}</div>
                </div>
                <span data-label="Assigned" style={{ fontWeight: 700, color: 'var(--text)', fontSize: 14 }}>{r.total_assigned || 0}</span>
                <span data-label="Completed" style={{ fontWeight: 700, color: 'var(--green)', fontSize: 14 }}>{r.completed || 0}</span>
                <span data-label="Pending" style={{ fontWeight: 700, color: 'var(--amber)', fontSize: 14 }}>{r.pending || 0}</span>
                <span data-label="Avg Score" style={{ fontWeight: 900, color: r.avg_score_given ? 'var(--pink)' : 'var(--text-muted)', fontSize: 14 }}>
                  {r.avg_score_given ? `★ ${r.avg_score_given}` : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ACCESS LOGS ── */}
      {activeTab === 'logs' && (
        <div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400, marginBottom: 14 }}>
            Every time a reviewer opens a film to review it, it's logged here — reviewer, film, IP address, and time. Kept for 24 months per the reviewer confidentiality agreement, then auto-deleted.
          </p>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
              <Search size={13} color="var(--text-muted)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
              <input value={viewLogsSearch} onChange={e => setViewLogsSearch(e.target.value)}
                placeholder="Search by reviewer or film name..."
                style={{ fontSize: 12, padding: '8px 10px 8px 30px', width: '100%', boxSizing: 'border-box' }} />
            </div>
            {viewLogsActionOptions.length > 1 && (
              <select value={viewLogsAction} onChange={e => setViewLogsAction(e.target.value)} style={{ fontSize: 12, padding: '8px 10px', width: 'auto' }}>
                <option value="">All categories</option>
                {viewLogsActionOptions.map(a => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
              </select>
            )}
            {(viewLogsSearch || viewLogsAction) && (
              <button onClick={() => { setViewLogsSearch(''); setViewLogsAction(''); }}
                style={{ background: 'none', color: 'var(--text-muted)', border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '8px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)' }}>
                Clear
              </button>
            )}
          </div>
          <div className="card" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
            <div className="admin-grid-header" style={{ gridTemplateColumns: '1fr 1fr 140px 180px', padding: '9px 16px', borderBottom: '1.5px solid var(--border)', fontSize: 10.5, color: 'var(--text-secondary)', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              <span>Reviewer</span><span>Film</span><span>IP Address</span><span>Accessed At</span>
            </div>
            {viewLogsLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
            ) : viewLogs.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                {(viewLogsSearch || viewLogsAction) ? 'No access logs match your search/filter.' : 'No film access logged yet.'}
              </div>
            ) : viewLogs.map((log, i) => (
              <div key={log.id} className="admin-grid-row" style={{ gridTemplateColumns: '1fr 1fr 140px 180px', padding: '11px 16px', borderBottom: i < viewLogs.length - 1 ? '1px solid var(--border)' : 'none', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{log.reviewer_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 400, overflowWrap: 'anywhere' }}>{log.reviewer_email}</div>
                </div>
                <div data-label="Film" style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{log.film_title || '—'}</div>
                <div data-label="IP Address" style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, fontFamily: 'monospace' }}>{log.ip_address || '—'}</div>
                <div data-label="Accessed At" style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>
                  {new Date(log.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
          </div>
          {viewLogsTotal > 50 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, marginTop: 14 }}>
              <button onClick={() => setViewLogsPage(p => Math.max(1, p - 1))} disabled={viewLogsPage === 1} className="btn-ghost" style={{ fontSize: 11, padding: '6px 14px' }}>← Prev</button>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Page {viewLogsPage} of {Math.ceil(viewLogsTotal / 50)}</span>
              <button onClick={() => setViewLogsPage(p => p + 1)} disabled={viewLogsPage >= Math.ceil(viewLogsTotal / 50)} className="btn-ghost" style={{ fontSize: 11, padding: '6px 14px' }}>Next →</button>
            </div>
          )}
        </div>
      )}

      {/* Consensus panel */}
      {consensusFor && <ConsensusPanel submissionId={consensusFor} onClose={() => setConsensusFor(null)} />}
    </div>
  );
}
